import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { connect } from 'react-redux'
import moment from 'moment'
import _map from 'lodash/map'
import _groupBy from 'lodash/groupBy'

import { FETCH_EVENT_TRAFFIC } from '../../../_common/redux/performance/actions'
import { fetchEventTrafficSubtable, fetchEventTrafficTable } from '../../../_common/core/http_services/events'
import { defaultDateFormat } from '../../../_common/core/validation/normalizers'
import { DATE_TRAFFIC_SUPPORT } from '../performance/constants/constants'
import { showAxiosError } from '../../utils/messenger'
import { stringFromParams } from './utils'

const asyncForEach = async (array, callback, props, trafficType) => {
  if (!trafficType) return {}

  const returnObj = {}
  for (let i = 0; i < array.length; i++) {
    const data = await callback(props.eventId, {
      group_by: array[i].group_by,
      page: array[i].page || '',
      preregistration_only: props.preregistration_only ? 'yes' : 'no',
      ...props.dates,
    })
    returnObj[stringFromParams(array[i])] = data
  }

  return returnObj
}

const TrafficWrapperContainer = ({
  trafficType = 'eventTraffic',
  event,
  showWarning,
  setShowWarning,
  Component,
  params,
  loadPageViews,
  ...rest
}) => {
  const { FETCH_EVENT_TRAFFIC } = rest

  const [trafficDataParams, setTrafficDataParams] = useState(params)
  const [startDate, setStartDate] = useState(null)
  const [endDate, setEndDate] = useState(null)
  const [formattedDate, setFormattedDate] = useState(null)
  const [trafficDataObj, setTrafficDataObj] = useState({})
  const [isLoadingTrafficData, setLoadingTrafficData] = useState(true)
  const [trafficTableData, setTrafficTableData] = useState([])
  const [trafficTableRowData, setTrafficTableRowData] = useState([])

  const [rawVisitsData, setRawVisitsData] = useState([])
  const [directVisitsData, setDirectVisitsData] = useState([])
  const [referralVisitsData, setReferralVisitsData] = useState([])
  const [uniqueVisitsData, setUniqueVisitsData] = useState([])

  const [detailsRowData, setDetailsRowData] = useState([])

  const dates = useMemo(
    () =>
      startDate && endDate
        ? { date_from: startDate.format('YYYY-MM-DD'), date_to: endDate.format('YYYY-MM-DD') }
        : {},
    [startDate, endDate],
  )

  const fetchTrafficData = useCallback(async () => {
    try {
      setLoadingTrafficData(true)
      const data = await asyncForEach(
        trafficDataParams,
        FETCH_EVENT_TRAFFIC,
        {
          eventId: event.id,
          dates,
          preregistration_only: trafficType !== 'eventTraffic',
        },
        trafficType,
      )

      setTrafficDataObj(data)
    } catch (e) {
      showAxiosError(e)
    } finally {
      setLoadingTrafficData(false)
    }
  }, [FETCH_EVENT_TRAFFIC, dates, event.id, trafficDataParams, trafficType])

  const fetchTrafficTableData = useCallback(async () => {
    const tableData = await fetchEventTrafficTable(event.id, {
      ...dates,
      preregistration_only: trafficType !== 'eventTraffic' ? 'yes' : 'no',
    })

    setTrafficTableData(tableData)
  }, [dates, event.id, trafficType])

  const fetchTrafficTableRowData = useCallback(
    async data => {
      const dateFormated = moment(data.createdAt, defaultDateFormat).format('YYYY-MM-DD')
      const tableRowData = await fetchEventTrafficSubtable(event.id, {
        date: dateFormated,
        preregistration_only: trafficType !== 'eventTraffic' ? 'yes' : 'no',
      })

      setTrafficTableRowData(tableRowData)
      setFormattedDate(dateFormated)

      return tableRowData
    },
    [event.id, trafficType],
  )

  const onDatesApply = useCallback((startDate, endDate) => {
    setStartDate(startDate || null)
    setEndDate(endDate || null)
  }, [])

  useEffect(() => {
    let intervalId = null

    fetchTrafficData()

    if (loadPageViews) {
      fetchTrafficTableData()
    }

    intervalId = setInterval(() => {
      fetchTrafficData()
    }, 600 * 1000)

    return () => {
      clearInterval(intervalId)
    }
  }, [event, fetchTrafficData, fetchTrafficTableData, loadPageViews])

  useEffect(() => {
    if (trafficDataObj && trafficTableData) {
      const trafficData = trafficDataObj[stringFromParams(trafficDataParams[0])] || []
      let total = 0
      let rawVisits = 0
      let uniqueVisits = 0
      let directVisits = 0
      let referralVisits = 0

      trafficTableData.forEach(row => {
        row.id = moment(row.createdAt, defaultDateFormat).format('YYYY-MM-DD')
        total += Number(row.total)
        referralVisits += Number(row.referralVisits)
        uniqueVisits += Number(row.uniqueVisits)
      })
      rawVisits = total - uniqueVisits
      directVisits = total - referralVisits

      if (!showWarning) {
        if (
          trafficData.find(row =>
            moment(row.created_at, defaultDateFormat).isBefore(
              moment(DATE_TRAFFIC_SUPPORT, defaultDateFormat),
            ),
          )
        ) {
          setShowWarning()
        }
      }

      setRawVisitsData(rawVisits)
      setDirectVisitsData(directVisits)
      setReferralVisitsData(referralVisits)
      setUniqueVisitsData(uniqueVisits)
    }
  }, [setShowWarning, showWarning, trafficDataObj, trafficDataParams, trafficTableData])

  useEffect(() => {
    const groups = _groupBy(trafficTableRowData, row =>
      moment(row.createdAt, defaultDateFormat).format('YYYY-MM-DD h:[00] a'),
    )
    const groupsResult = _map(groups, rows => {
      const newDetailRow = {
        total: 0,
        rawVisits: 0,
        uniqueVisits: 0,
        directVisits: 0,
        referralVisits: 0,
        createdAt: rows[0].createdAt,
      }
      rows.forEach(row => {
        newDetailRow.total += Number(row.total)
        newDetailRow.referralVisits += Number(row.referralVisits)
        newDetailRow.uniqueVisits++
      })
      newDetailRow.rawVisits = newDetailRow.total - newDetailRow.uniqueVisits
      newDetailRow.directVisits = newDetailRow.total - newDetailRow.referralVisits
      return newDetailRow
    })

    setDetailsRowData([
      {
        id: formattedDate,
        type: 'detailRow',
        data: groupsResult,
      },
    ])
  }, [formattedDate, trafficTableRowData])

  return (
    <Component
      event={event}
      trafficType={trafficType}
      showWarning={showWarning}
      setShowWarning={setShowWarning}
      trafficDataObj={trafficDataObj}
      trafficTableData={trafficTableData}
      trafficTableRowData={trafficTableRowData}
      rawVisitsData={rawVisitsData}
      directVisitsData={directVisitsData}
      isLoadingTrafficData={isLoadingTrafficData}
      referralVisitsData={referralVisitsData}
      uniqueVisitsData={uniqueVisitsData}
      detailsRowData={detailsRowData}
      startDate={startDate}
      endDate={endDate}
      params={trafficDataParams}
      fetchTrafficData={fetchTrafficData}
      getDetailRowData={fetchTrafficTableRowData}
      onDatesApply={onDatesApply}
      setTrafficDataParams={setTrafficDataParams}
    />
  )
}

const TrafficWrapper = connect(null, { FETCH_EVENT_TRAFFIC })(TrafficWrapperContainer)

export { TrafficWrapper }
